import { useEffect, useMemo, useState } from 'react';
import { Grid, Switch, Tooltip } from '@mui/material';

import { FullPageLoader, MDBox, MDTypography } from '@components';
import {
  AvailableSettings,
  ChartSettings,
  VisualSettings
} from '@DStypes/chart';

import { DATE_GROUP } from '@constants/common';
import SortOrder from './components/SortOrder';
import ChangeDateGroup from './components/ChangeDateGroup';
import { sendAmplitudeData } from '@utils/amplitude';
import {
  CHANGE_STACKING,
  CLICKED_NUMBER_INTERVAL,
  CLICKED_SHOW_AS_PERCENTAGE_TOGGLE,
  CLICKED_SHOW_EMPTY_VALUES,
  REMOVED_NUMBER_INTERVAL
} from '@constants/eventNames';
import { getXAxisLabelsExcluded } from '@utils/chart';
import Meter from './components/Meter';

interface Props {
  data: any[];
  config: any;
  filters: VisualSettings | null;
  setFilters: React.Dispatch<React.SetStateAction<VisualSettings | null>>;
  isPie?: boolean;
  isBar?: boolean;
  hasSegments?: boolean;
  height?: number;
  isChartLoading: boolean;
  isDate: boolean;
  dateGroup?: DATE_GROUP | '';
  setDateGroup: (dateGroup: DATE_GROUP | '') => void;
  setIsManuallySetStackChart: React.Dispatch<React.SetStateAction<boolean>>;
  setIsManuallySetLegends: React.Dispatch<React.SetStateAction<boolean>>;
  isDateTime: boolean;
  isNumberSelf: boolean;
  stackColumnName: string;
  isEditChart?: boolean;
  isKpiChart?: boolean;
  xAxisType: string;
  availableSettings: AvailableSettings;
}

const Settings: React.FC<Props> = ({
  data,
  config,
  filters,
  setFilters,
  isPie,
  height,
  hasSegments = false,
  isChartLoading,
  isBar,
  isDate: isDateAxis,
  isDateTime,
  dateGroup,
  setDateGroup,
  setIsManuallySetStackChart,
  setIsManuallySetLegends,
  stackColumnName,
  isEditChart = false,
  isKpiChart = false,
  xAxisType,
  availableSettings = {}
}) => {
  const [isDateGroupChanged, setDateGroupChanged] = useState<boolean>(false);
  const chartSettings = useMemo(
    () => Object.keys(availableSettings),
    [availableSettings]
  );

  const [settingsStatus, setSettingsStatus] = useState<any>({
    [ChartSettings.STACKED]:
      chartSettings.includes(ChartSettings.STACKED) && hasSegments,
    [ChartSettings.SHOW_LEGEND]:
      chartSettings.includes(ChartSettings.SHOW_LEGEND) && hasSegments,
    [ChartSettings.SORT_ORDER]: chartSettings.includes(
      ChartSettings.SORT_ORDER
    ),
    [ChartSettings.SHOW_EMPTY_VALUES]: chartSettings.includes(
      ChartSettings.SHOW_EMPTY_VALUES
    ),
    [ChartSettings.DATE_INTERVAL]: chartSettings.includes(
      ChartSettings.DATE_INTERVAL
    ),
    [ChartSettings.NUMBER_GROUPING]: chartSettings.includes(
      ChartSettings.NUMBER_GROUPING
    )
  });

  useEffect(() => {
    setSettingsStatus({
      [ChartSettings.STACKED]:
        chartSettings.includes(ChartSettings.STACKED) && hasSegments,
      [ChartSettings.SHOW_LEGEND]:
        chartSettings.includes(ChartSettings.SHOW_LEGEND) && hasSegments,
      [ChartSettings.SORT_ORDER]: chartSettings.includes(
        ChartSettings.SORT_ORDER
      ),
      [ChartSettings.SHOW_EMPTY_VALUES]: chartSettings.includes(
        ChartSettings.SHOW_EMPTY_VALUES
      ),
      [ChartSettings.DATE_INTERVAL]: chartSettings.includes(
        ChartSettings.DATE_INTERVAL
      ),
      [ChartSettings.NUMBER_GROUPING]: chartSettings.includes(
        ChartSettings.NUMBER_GROUPING
      )
    });
  }, [chartSettings, hasSegments]);

  const xAxisField = isPie
    ? config?.series?.[0]?.dataFields?.category
    : isBar
    ? config?.yAxes?.[0]?.dataFields?.category
    : config?.xAxes?.[0]?.dataFields?.category;

  useEffect(() => {
    if (isDateAxis || isDateTime) {
      const xAxisLabelsExcluded = getXAxisLabelsExcluded({
        isDate: isDateAxis || isDateTime,
        isDateGroupChanged,
        dateRange: filters?.filters?.dateRange,
        data,
        xAxisField,
        setDateGroupChanged
      });

      if (xAxisLabelsExcluded)
        setFilters((prev: any) => ({
          ...prev,
          filters: {
            ...prev?.filters,
            xAxisLabelsExcluded: xAxisLabelsExcluded
          }
        }));
    }
  }, [data?.[0]?.[xAxisField]]);

  // Hook to update empty values for non kpi charts
  // useEffect(() => {
  //   if (!isKpiChart)
  //     setFilters({
  //       ...filters,
  //       showEmptyValues:
  //         isDateAxis || isDateTime || isNumberSelf
  //           ? false
  //           : filters?.showEmptyValues
  //     });
  // }, [isDateAxis, isDateTime, isNumberSelf]);

  useEffect(() => {
    if (typeof data?.[0]?.[xAxisField] === 'number') {
      const xAxisLabelsExcluded: any = [];

      if (
        filters?.filters?.xAxisLabelsExcluded.some(
          (element) => typeof element === 'string'
        ) &&
        !filters?.filters?.xAxisLabelsExcluded?.includes('Empty')
        //Condition added to handle the empty values for KPI Charts
      ) {
        data?.forEach((label) => {
          filters?.filters?.xAxisLabelsExcluded.forEach((excludedLabel) => {
            const endpoints = excludedLabel.split(' - ');

            const start = parseInt(endpoints[0].replace(/,/g, ''));
            const end = parseInt(endpoints[1].replace(/,/g, ''));

            if (label?.[xAxisField] >= start && label?.[xAxisField] < end)
              xAxisLabelsExcluded.push(label?.[xAxisField]);
          });
        });

        setFilters((prev: any) => ({
          ...prev,
          filters: {
            ...prev?.filters,
            xAxisLabelsExcluded: xAxisLabelsExcluded
          }
        }));
      }
    }

    if (
      filters?.autoGroupNumbers &&
      filters.filters?.xAxisLabelsExcluded.some(
        (element) => typeof element === 'number'
      )
    )
      setFilters((prev: any) => ({
        ...prev,
        filters: {
          ...prev?.filters,
          xAxisLabelsExcluded: []
        }
      }));
  }, [data]);

  return (
    <>
      <MDBox
        width='100%'
        maxWidth='100%'
        height={`${(height || 95) - 95}px`}
        sx={{ paddingBottom: '16px' }}
      >
        {!isEditChart ? (
          <MDTypography
            variant='h5'
            color='info'
            fontWeight='bold'
            sx={{
              textAlign: 'center',
              paddingBottom: '10px'
            }}
          >
            View Settings
            <MDBox
              sx={{
                borderBottom: '1px solid #000',
                paddingBottom: '10px',
                width: '95%',
                margin: 'auto'
              }}
            ></MDBox>
          </MDTypography>
        ) : (
          <></>
        )}
        <MDBox
          opacity={isChartLoading ? 0 : 1}
          display='flex'
          flexDirection={'column'}
        >
          {isKpiChart ? (
            <>
              <MDBox
                sx={{ display: 'flex', alignItems: 'center' }}
                height='45px'
              >
                <Tooltip
                  title={
                    filters?.showEmptyValues
                      ? 'Click to hide empty values'
                      : 'Click to show empty values'
                  }
                  placement='top'
                  sx={{ pointerEvents: 'auto' }}
                >
                  <div>
                    <Switch
                      checked={filters?.showEmptyValues}
                      onChange={(e, value) => {
                        if (value)
                          sendAmplitudeData(CLICKED_SHOW_EMPTY_VALUES, {
                            x_axis_data_type: xAxisType
                          });

                        setFilters({
                          ...filters,
                          showEmptyValues: value
                        });
                      }}
                    />
                  </div>
                </Tooltip>
                <MDTypography variant='sm' color='info' ml={1}>
                  Show Empty Values
                </MDTypography>
              </MDBox>
              <Meter
                kpiTarget={filters?.kpiTarget}
                setKpiTarget={(target) =>
                  setFilters({ ...filters, kpiTarget: target })
                }
              />
            </>
          ) : (
            <>
              <MDBox
                sx={{ display: 'flex', alignItems: 'center', height: '45px' }}
              >
                <Tooltip
                  title={
                    hasSegments
                      ? filters?.stackChart
                        ? 'Click to view chart as Unstacked'
                        : 'Click to view chart as Stacked'
                      : 'Select a column name on Split By to enable stacking of chart'
                  }
                  placement='top'
                  sx={{ pointerEvents: 'auto' }}
                >
                  <div>
                    <Switch
                      checked={
                        !settingsStatus[ChartSettings.STACKED]
                          ? false
                          : filters?.stackChart
                      }
                      onChange={(e, value) => {
                        setFilters({
                          ...filters,
                          stackChart: value
                        });
                        setIsManuallySetStackChart(true);
                        sendAmplitudeData(CHANGE_STACKING, {
                          'New Stacking':
                            value === true ? 'Stacked' : 'Non Stacked'
                        });
                      }}
                      disabled={!settingsStatus[ChartSettings.STACKED]}
                    />
                  </div>
                </Tooltip>

                <Tooltip
                  title={stackColumnName.length > 39 ? stackColumnName : ''}
                  placement='top-start'
                >
                  <MDTypography
                    variant='sm'
                    color='info'
                    opacity={settingsStatus[ChartSettings.STACKED] ? 1 : 0.5}
                    ml={1}
                    overflow='hidden'
                    textOverflow='ellipsis'
                    maxWidth='280px'
                  >
                    {stackColumnName}
                  </MDTypography>
                </Tooltip>
              </MDBox>
              <MDBox
                sx={{ display: 'flex', alignItems: 'center', height: '45px' }}
              >
                <Tooltip
                  title={
                    hasSegments
                      ? filters?.showLegends
                        ? 'Click to hide legends'
                        : 'Click to show legends'
                      : 'Select a column name on Split By to enable legends'
                  }
                  placement='top'
                  sx={{ pointerEvents: 'auto' }}
                >
                  <div>
                    <Switch
                      checked={
                        !settingsStatus[ChartSettings.SHOW_LEGEND]
                          ? false
                          : filters?.showLegends
                      }
                      onChange={(e, value) => {
                        setFilters({
                          ...filters,
                          showLegends: value
                        });
                        setIsManuallySetLegends(true);
                      }}
                      disabled={!settingsStatus[ChartSettings.SHOW_LEGEND]}
                    />
                  </div>
                </Tooltip>
                <MDTypography
                  variant='sm'
                  color='info'
                  opacity={settingsStatus[ChartSettings.SHOW_LEGEND] ? 1 : 0.5}
                  ml={1}
                >
                  Show/Hide Legends
                </MDTypography>
              </MDBox>
              <SortOrder
                isDisabled={!settingsStatus[ChartSettings.SORT_ORDER]}
                setSortOrder={(sortOrder) =>
                  setFilters({ ...filters, sortOrder: sortOrder })
                }
                sortOrder={filters?.sortOrder || ''}
              />
              <MDBox
                sx={{ display: 'flex', alignItems: 'center', height: '45px' }}
              >
                <Tooltip
                  title={
                    !settingsStatus[ChartSettings.SHOW_EMPTY_VALUES]
                      ? ''
                      : filters?.showEmptyValues
                      ? 'Click to hide empty values'
                      : 'Click to show empty values'
                  }
                  placement='top'
                  sx={{ pointerEvents: 'auto' }}
                >
                  <div>
                    <Switch
                      checked={
                        !settingsStatus[ChartSettings.SHOW_EMPTY_VALUES]
                          ? false
                          : filters?.showEmptyValues
                      }
                      onChange={(e, value) => {
                        if (value)
                          sendAmplitudeData(CLICKED_SHOW_EMPTY_VALUES, {
                            x_axis_data_type: xAxisType
                          });
                        setFilters({
                          ...filters,
                          showEmptyValues: value
                        });
                      }}
                      disabled={
                        !settingsStatus[ChartSettings.SHOW_EMPTY_VALUES]
                      }
                    />
                  </div>
                </Tooltip>
                <MDTypography
                  variant='sm'
                  color='info'
                  opacity={
                    settingsStatus[ChartSettings.SHOW_EMPTY_VALUES] ? 1 : 0.5
                  }
                  ml={1}
                >
                  Show Empty Values
                </MDTypography>
              </MDBox>
              <ChangeDateGroup
                setDateGroup={(newGroup) => {
                  setDateGroup(newGroup);
                  setDateGroupChanged(true);
                }}
                dateGroup={dateGroup || ''}
                isDateTime={isDateTime}
                disabled={!settingsStatus[ChartSettings.DATE_INTERVAL]}
                data={data}
                filters={
                  filters?.filtersToggle && filters?.filters
                    ? filters?.filters
                    : null
                }
                xAxisField={xAxisField}
              />
              <MDBox
                sx={{ display: 'flex', alignItems: 'center', height: '45px' }}
              >
                <Tooltip
                  title={
                    !settingsStatus[ChartSettings.NUMBER_GROUPING]
                      ? 'Only available when a Number column is shown'
                      : filters?.autoGroupNumbers
                      ? 'Click to disable auto group'
                      : 'Click to enable auto group'
                  }
                  placement='top'
                  sx={{ pointerEvents: 'auto' }}
                >
                  <div>
                    <Switch
                      checked={
                        !settingsStatus[ChartSettings.NUMBER_GROUPING]
                          ? false
                          : filters?.autoGroupNumbers
                      }
                      onChange={(e, value) => {
                        if (!value) sendAmplitudeData(REMOVED_NUMBER_INTERVAL);
                        else
                          sendAmplitudeData(CLICKED_NUMBER_INTERVAL, {
                            data_type_combination: 'Number & Number'
                          });
                        setFilters({
                          ...filters,
                          autoGroupNumbers: value
                        });
                      }}
                      disabled={!settingsStatus[ChartSettings.NUMBER_GROUPING]}
                    />
                  </div>
                </Tooltip>
                <MDTypography
                  variant='sm'
                  color='info'
                  opacity={
                    settingsStatus[ChartSettings.NUMBER_GROUPING] ? 1 : 0.5
                  }
                  ml={1}
                >
                  Show Number as Interval
                </MDTypography>
              </MDBox>
              <MDBox
                sx={{ display: 'flex', alignItems: 'center', height: '45px' }}
              >
                <Tooltip
                  title={
                    filters?.showAsPercentage
                      ? 'Select to view chart as number instead of %'
                      : 'Select to view chart as % instead of number'
                  }
                  placement='top'
                  sx={{ pointerEvents: 'auto' }}
                >
                  <div>
                    <Switch
                      checked={filters?.showAsPercentage}
                      onChange={(e, value) => {
                        sendAmplitudeData(CLICKED_SHOW_AS_PERCENTAGE_TOGGLE, {
                          status: value ? 'on' : 'off'
                        });
                        setFilters({
                          ...filters,
                          showAsPercentage: value
                        });
                      }}
                    />
                  </div>
                </Tooltip>
                <MDTypography variant='sm' color='info' ml={1}>
                  Show chart as % value
                </MDTypography>
              </MDBox>
            </>
          )}
        </MDBox>
      </MDBox>
      {isChartLoading && (
        <Grid position='absolute' top={300} right={160}>
          <FullPageLoader height='10%' />
        </Grid>
      )}
    </>
  );
};

export default Settings;
